import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useUnsavedChangesContext } from '../contexts/UnsavedChangesContext';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../components/ui/alert-dialog';
import { VOHKLogo, VOHKLogoWithText } from '../components/VOHKLogo';
import { LayoutDashboard, Package, LogOut, User, Database, Menu, X, Beaker, MapPin, Trash2 } from 'lucide-react';

// Any open Radix dialog/alert-dialog/select/popover/listbox should handle its own Escape close
const isAnyOverlayOpen = () => {
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="listbox"], [data-radix-popper-content-wrapper]'
  );
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false
  );
  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const listener = () => setIsDesktop(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);
  return isDesktop;
}

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const { isDirty, setDirty } = useUnsavedChangesContext();
  const isDesktop = useIsDesktop();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [focusedNavIndex, setFocusedNavIndex] = useState(-1);
  const navItemRefs = useRef([]);

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Reset keyboard focus whenever the sidebar closes
  useEffect(() => {
    if (!sidebarOpen) setFocusedNavIndex(-1);
  }, [sidebarOpen]);

  // Block the browser's own Ctrl+S / Cmd+S "Save Page As" everywhere in the app;
  // pages/dialogs that support saving register their own useSaveShortcut(onSave) handler
  useEffect(() => {
    const blockBrowserSave = (e) => {
      const key = e.key?.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === 's') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', blockBrowserSave);
    return () => document.removeEventListener('keydown', blockBrowserSave);
  }, []);

  // Move real DOM focus to match the keyboard-highlighted nav item
  useEffect(() => {
    if (sidebarOpen && focusedNavIndex >= 0) {
      navItemRefs.current[focusedNavIndex]?.focus();
    }
  }, [sidebarOpen, focusedNavIndex]);

  const goBack = () => {
    // Dashboard is the app's homepage - Esc navigation stops here
    if (location.pathname === '/dashboard') return;
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleDiscardConfirm = () => {
    setDirty(false);
    setShowDiscardConfirm(false);
    goBack();
  };

  // Global Escape key: close sidebar, let open dialogs/dropdowns close themselves,
  // confirm discard on dirty forms, otherwise navigate to the previous page
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      // Radix Select/Popover/DropdownMenu call preventDefault on their own Escape
      // close handler (capture phase) before this bubble-phase listener runs
      if (e.defaultPrevented) return;
      if (sidebarOpen) {
        setSidebarOpen(false);
        return;
      }
      if (isAnyOverlayOpen()) {
        return;
      }
      if (isDirty) {
        setShowDiscardConfirm(true);
        return;
      }
      goBack();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen, isDirty, location.pathname]);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/orders', label: 'Orders', icon: Package },
    { path: '/field-activity', label: 'Field Activity', icon: MapPin },
  ];
  
  // Check if user can manage master data (admins + jay, jagruti, shruti)
  const canManageMasterData = () => {
    if (user?.role === 'admin' || user?.role === 'Super Admin') return true;
    const allowedUsers = ['jay', 'jagruti', 'shruti'];
    return allowedUsers.includes(user?.username?.toLowerCase());
  };

  const isAppAdmin = user?.role === 'admin' || user?.role === 'Super Admin';

  // Show Master Data for authorized users only; Recycle Bin for admins only
  const adminNavItems = [
    ...(canManageMasterData() ? [
      { path: '/master-data', label: 'Master Data', icon: Database },
      { path: '/sample-master', label: 'Sample Master', icon: Beaker },
    ] : []),
    ...(isAppAdmin ? [
      { path: '/recycle-bin', label: 'Recycle Bin', icon: Trash2 },
    ] : []),
  ];

  // Flat, ordered list used for keyboard (arrow key) navigation of the sidebar
  const allNavItems = [
    ...navItems,
    ...adminNavItems,
    { path: null, label: 'Logout', icon: LogOut, action: 'logout' },
  ];

  // Desktop mouseless sidebar: Left Arrow opens it, Up/Down move through options, Enter selects
  useEffect(() => {
    const handleSidebarKeyNav = (e) => {
      if (!isDesktop) return;
      const target = e.target;
      const isTypingTarget =
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        target.isContentEditable ||
        target.getAttribute?.('role') === 'combobox';
      if (isTypingTarget || isAnyOverlayOpen()) return;

      if (e.key === 'ArrowLeft') {
        if (!sidebarOpen) {
          e.preventDefault();
          setSidebarOpen(true);
          setFocusedNavIndex(0);
        }
        return;
      }
      if (!sidebarOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedNavIndex((prev) => Math.min(prev + 1, allNavItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedNavIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && focusedNavIndex >= 0) {
        e.preventDefault();
        const item = allNavItems[focusedNavIndex];
        if (item.action === 'logout') {
          handleLogout();
        } else {
          navigate(item.path);
        }
        setSidebarOpen(false);
      }
    };
    document.addEventListener('keydown', handleSidebarKeyNav);
    return () => document.removeEventListener('keydown', handleSidebarKeyNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, sidebarOpen, focusedNavIndex]);

  return (
    <div className="flex h-screen bg-maroon-50">
      {/* Top Header Bar - Always Visible */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-maroon-950 to-maroon-900 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-maroon-800 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <VOHKLogoWithText size="md" dark={true} className="ml-1" />
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <span className="text-white font-medium capitalize">{user?.username}</span>
            <span className="text-xs text-maroon-300">({user?.role})</span>
          </div>
          <Button
            data-testid="header-logout-button"
            onClick={handleLogout}
            size="sm"
            className="bg-white/10 hover:bg-white/20 text-white border border-white/30"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Logout
          </Button>
        </div>
      </div>

      {/* Overlay - Visible when sidebar is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 mt-14"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Slides in from left */}
      <aside
        inert={!sidebarOpen}
        aria-hidden={!sidebarOpen}
        className={`
          fixed h-full bg-gradient-to-b from-maroon-950 to-maroon-900 text-white flex flex-col z-50
          transition-transform duration-300 ease-in-out
          w-72 top-14
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            const isFocused = focusedNavIndex === index;
            return (
              <Link
                key={item.path}
                ref={(el) => (navItemRefs.current[index] = el)}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                data-focused={isFocused}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 outline-none ${
                  isFocused
                    ? 'bg-maroon-700 text-white font-medium ring-2 ring-white'
                    : isActive
                    ? 'bg-maroon-800 text-white font-medium'
                    : 'text-maroon-200 hover:bg-maroon-800 hover:text-white'
                }`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
          
          {adminNavItems.length > 0 && (
            <>
              <div className="border-t border-maroon-800 my-2"></div>
              {adminNavItems.map((item, adminIndex) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                const index = navItems.length + adminIndex;
                const isFocused = focusedNavIndex === index;
                return (
                  <Link
                    key={item.path}
                    ref={(el) => (navItemRefs.current[index] = el)}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    data-focused={isFocused}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 outline-none ${
                      isFocused
                        ? 'bg-maroon-700 text-white font-medium ring-2 ring-white'
                        : isActive
                        ? 'bg-maroon-800 text-white font-medium'
                        : 'text-maroon-200 hover:bg-maroon-800 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-maroon-800">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="h-8 w-8 rounded-full bg-maroon-800 flex items-center justify-center">
              <User className="h-4 w-4 text-maroon-200" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate capitalize">{user?.username}</p>
              <p className="text-xs text-maroon-300">{user?.role}</p>
            </div>
          </div>
          <Button
            ref={(el) => (navItemRefs.current[allNavItems.length - 1] = el)}
            data-testid="logout-button"
            data-focused={focusedNavIndex === allNavItems.length - 1}
            onClick={handleLogout}
            variant="outline"
            className={`w-full border-maroon-700 text-maroon-200 hover:bg-maroon-800 hover:text-white transition-colors duration-200 outline-none ${
              focusedNavIndex === allNavItems.length - 1 ? 'ring-2 ring-white bg-maroon-800' : ''
            }`}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content - Reduced padding on mobile */}
      <main inert={sidebarOpen} className="flex-1 overflow-y-auto pt-14">
        <div className={location.pathname === '/orders' ? 'p-2 sm:p-4 lg:py-4 lg:px-4' : 'p-2 sm:p-4 lg:p-8'}>{children}</div>
      </main>

      {/* Esc-to-go-back discard confirmation */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent data-testid="discard-changes-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes on this page. Going back now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-changes-cancel-btn">Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              data-testid="discard-changes-confirm-btn"
              onClick={handleDiscardConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
